<script lang="ts">
  import { dispatcherApi } from '../utils/api';

  interface Props {
    delivery: any;
    driver_id: string;
    onDeliveryClick?: (delivery: any) => void;
    onDeliveryUpdate?: (deliveryId: string, updates: any) => void;
  }

  let { delivery, driver_id, onDeliveryClick, onDeliveryUpdate }: Props = $props();
  let isExpanded = $state(false);
  let isSaving = $state(false);
  let saveResult = $state<'success' | 'error' | null>(null);

  // Editable fields — delivery.status contains the raw DOT_Estado value
  let editStatus = $state(delivery.status);
  let editNotes = $state(delivery.notes || '');

  // Map DOT_Estado values to display labels (estados reales de la BD)
  const statusOptions = [
    { value: 'EN DESPACHO', label: '📦 En Despacho', color: 'text-yellow-400' },
    { value: 'EN TRANSFERENCIA', label: '🚚 En Transferencia', color: 'text-blue-400' },
    { value: 'EN CONTENEDOR', label: '📦 En Contenedor', color: 'text-gray-400' },
    { value: 'EN BINS', label: '🗃️ En Bins', color: 'text-gray-400' },
    { value: 'EN SUPRACONTENEDOR', label: '📦 En Supracontenedor', color: 'text-gray-400' },
    { value: 'EN RECEPCION', label: '✅ En Recepción', color: 'text-green-400' },
    { value: 'EN DESCARGO', label: '✅ En Descargo', color: 'text-green-400' },
    { value: 'ENTREGADO', label: '✅ Entregado', color: 'text-green-400' },
    { value: 'EN SOBRANCIA', label: '❌ En Sobrancia', color: 'text-red-400' },
    { value: 'EN RETENCION', label: '⏸️ En Retención', color: 'text-red-400' },
  ];

  function getStatusColor(s: string) {
    const option = statusOptions.find(opt => opt.value === s);
    return option?.color || 'text-gray-400';
  }

  function toggleEdit() {
    isExpanded = !isExpanded;
    if (isExpanded) {
      editStatus = delivery.status;
      editNotes = delivery.notes || '';
      saveResult = null;
    }
  }

  function handleDeliveryClick() {
    if (onDeliveryClick) {
      onDeliveryClick(delivery);
    }
  }

  async function saveDelivery() {
    if (isSaving) return;

    isSaving = true;
    saveResult = null;
    try {
      const response = await dispatcherApi.reassignDelivery(delivery.id, {
        current_driver_id: driver_id,
        status: editStatus,
        notes: editNotes
      });

      // El endpoint responde 200 tanto cuando modificó como cuando no encontró
      // el OT. Distinguimos por `data.status`: 'success' = guardado real,
      // 'no_change' = matching document faltante o sin cambios (antes lo
      // pintábamos como "✅ Guardado" y el operador creía que había guardado).
      const backendStatus = (response.data as any)?.status;
      const actuallySaved = response.success && backendStatus !== 'no_change';

      if (actuallySaved) {
        // Usar callback para notificar al padre en lugar de mutar directamente
        if (onDeliveryUpdate) {
          onDeliveryUpdate(delivery.id, {
            status: editStatus,
            notes: editNotes
          });
        }
        saveResult = 'success';
        setTimeout(() => {
          isExpanded = false;
          saveResult = null;
        }, 1000);
      } else {
        console.warn('⚠️ Reassign returned no_change or non-success:', response);
        saveResult = 'error';
      }
    } catch (error) {
      console.error('❌ Error guardando entrega:', error);
      saveResult = 'error';
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="bg-white/5 border border-white/10 rounded p-2 text-xs mb-2">
  <button
    type="button"
    class="w-full flex items-start justify-between gap-2 cursor-pointer hover:bg-white/10 p-1 rounded transition text-left"
    onclick={() => {
      handleDeliveryClick();
      toggleEdit();
    }}
  >
    <div class="flex-1 min-w-0">
      <p class="font-bold text-white">OT-{delivery.id || 'N/A'}</p>
      <p class="text-gray-400 truncate">{delivery.client || 'Sin cliente'}</p>
      <p class="text-gray-500 truncate text-[10px]">{delivery.address || 'Sin dirección'}</p>
    </div>
    <div class="flex flex-col items-end gap-1 flex-shrink-0">
      <span class="text-[10px] font-bold {getStatusColor(delivery.status)}">
        {delivery.status}
      </span>
      <span class="text-gray-500 transition-transform text-[10px]" class:rotate-180={isExpanded}>▼</span>
    </div>
  </button>

  {#if isExpanded}
    <div class="border-t border-white/10 mt-2 pt-2 space-y-2">
      <div>
        <label class="text-gray-500 text-[10px] mb-1 block">Dirección</label>
        <div class="w-full px-2 py-1.5 bg-black border border-white/20 rounded text-xs text-gray-300 break-words">
          {delivery.address || 'Sin dirección'}
        </div>
      </div>

      <div>
        <label class="text-gray-500 text-[10px] mb-1 block">Estado</label>
        <select
          bind:value={editStatus}
          class="w-full px-2 py-1.5 bg-black border border-white/20 rounded text-xs text-white focus:outline-none focus:border-blue-500"
        >
          {#each statusOptions as opt}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>

      <div>
        <label class="text-gray-500 text-[10px] mb-1 block">Notas</label>
        <textarea
          bind:value={editNotes}
          placeholder="Notas..."
          class="w-full px-2 py-1 bg-black border border-white/20 rounded text-xs text-white placeholder-gray-600 resize-none h-12 focus:outline-none focus:border-blue-500"
        ></textarea>
      </div>

      <button
        onclick={saveDelivery}
        disabled={isSaving}
        class="w-full px-2 py-1.5 rounded text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed
          {saveResult === 'success' ? 'bg-green-500/30 text-green-400' :
           saveResult === 'error' ? 'bg-red-500/30 text-red-400' :
           'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'}"
      >
        {#if isSaving}
          <span class="flex items-center justify-center gap-1">
            <span class="animate-spin inline-block w-3 h-3 border border-blue-400 border-t-transparent rounded-full"></span>
            Guardando...
          </span>
        {:else if saveResult === 'success'}
          ✅ Guardado
        {:else if saveResult === 'error'}
          ❌ Error al guardar
        {:else}
          💾 Guardar cambios
        {/if}
      </button>
    </div>
  {/if}
</div>
